// src/components/character/printCharacterSheet.js
import React from "react";
import ReactDOMServer from "react-dom/server";
import PrintableCharacterSheet from "./PrintableCharacterSheet";

/** Resolve the public URL base (works in CRA & Vite) */
const BASE =
    (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.BASE_URL) ||
    (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) ||
    "";

/** Fetch the CSS file as text once and cache it */
let __printCssCache = null;
async function getPrintCssText() {
    if (__printCssCache) return __printCssCache;
    const url = `${BASE.replace(/\/$/, "")}/print/character-sheet.print.css`;
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`Failed to load print CSS: ${res.status}`);
    __printCssCache = await res.text();
    return __printCssCache;
}

/** Build a hidden iframe, inject CSS (as <style>), load Google Fonts, wait, print */
async function printViaIframe(html) {
    const iframe = document.createElement("iframe");
    Object.assign(iframe.style, {
        position: "fixed",
        top: "-10000px",
        left: "-10000px",
        width: "1px",
        height: "1px",
        opacity: "0",
        pointerEvents: "none",
        border: "0",
    });
    document.body.appendChild(iframe);

    const cssText = await getPrintCssText();

    const srcdoc = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Print Character Sheet</title>
    <!-- Preconnects help Google Fonts start faster -->
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <style>${cssText}</style>
  </head>
  <body class="theme-print">
    ${html}
  </body>
</html>`;

    const onFrameLoad = async () => {
        const win = iframe.contentWindow;
        const doc = win.document;

        // Inject Google Fonts stylesheet (Cinzel Decorative + Cinzel, 400 & 700)
        const link = doc.createElement("link");
        link.rel = "stylesheet";
        link.href =
            "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=Cinzel+Decorative:wght@400;700&display=swap";

        // Wait for stylesheet load (with a timeout fallback)
        const stylesheetLoaded = new Promise((resolve) => {
            let done = false;
            const finish = () => { if (!done) { done = true; resolve(); } };
            link.onload = finish;
            link.onerror = finish;
            setTimeout(finish, 1500);
        });
        doc.head.appendChild(link);
        await stylesheetLoaded;

        // Ensure React had time to render & layout so the font faces are actually referenced
        await new Promise((r) => win.requestAnimationFrame(() => win.requestAnimationFrame(r)));

        // Explicitly request both weights so bold text uses Cinzel Decorative too
        try {
            if (doc.fonts && doc.fonts.load) {
                await Promise.allSettled([
                    doc.fonts.load('400 16px "Cinzel Decorative"'),
                    doc.fonts.load('700 16px "Cinzel Decorative"'),
                    doc.fonts.load('400 16px "Cinzel"'),
                    doc.fonts.load('700 16px "Cinzel"'),
                ]);
                await doc.fonts.ready;
            } else {
                await new Promise((r) => setTimeout(r, 300));
            }
        } catch {
            /* ignore font load errors */
        }

        // Print and cleanup
        try { win.focus(); } catch {}
        win.print();
        setTimeout(() => iframe.remove(), 1000);
    };

    iframe.addEventListener("load", onFrameLoad, { once: true });
    iframe.srcdoc = srcdoc;
}

/** Public API */
export async function printCharacterSheet({ character, races, skillsets, availableSkills }) {
    const markup = ReactDOMServer.renderToStaticMarkup(
        <PrintableCharacterSheet
            character={character}
            races={races}
            skillsets={skillsets}
            availableSkills={availableSkills}
        />
    );
    await printViaIframe(markup);
}
